import visa from "assets/visa.svg";
import mada from "assets/mada.svg";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import PayfortPaymentForm from "./PayfortPaymentForm";
import {
  formatCreditCardNumber,
  formatCVC,
  formatExpirationDate,
} from "./paymentForm.utils";
import "./PaymentForm.scss";

type PartnerPayfortCheckoutProps = {
  payForm: string;
};

export default function PartnerPayfortCheckout({ payForm }: PartnerPayfortCheckoutProps) {
  const { t } = useTranslation("translation");

  const paymentMethods = [
    { name: t("mada_bank_card", "Mada bank card"), img: mada, id: 1 },
    { name: t("creditCard", "Credit card"), img: visa, id: 2 },
  ];

  const [active, setActive] = useState(1);
  const [val, setVal] = useState({
    number: "",
    name: "",
    expiry: "",
    cvc: "",
    issuer: "",
  });
  const [errors, setErrors] = useState({
    number: "",
    name: "",
    expiry: "",
    cvc: "",
  });
  const [touched, setTouched] = useState({
    number: false,
    name: false,
    expiry: false,
    cvc: false,
  });

  const validateCardNumber = (number: string): string => {
    const cleaned = number.replace(/\s/g, "");
    if (!cleaned) return t("cardNumberRequired", "Card number is required");
    if (cleaned.length < 13 || cleaned.length > 19)
      return t("invalidCardNumber", "Invalid card number");
    let sum = 0;
    let isEven = false;
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned.charAt(i), 10);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    if (sum % 10 !== 0) return t("invalidCardNumber", "Invalid card number");
    return "";
  };

  const validateExpiry = (expiry: string): string => {
    if (!expiry) return t("expiryRequired", "Expiry date is required");
    const [month, year] = expiry.split("/");
    if (!month || !year || month.length !== 2 || year.length !== 2)
      return t("invalidExpiryFormat", "Invalid expiry format (MM/YY)");
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    if (monthNum < 1 || monthNum > 12) return t("invalidMonth", "Invalid month");
    const now = new Date();
    const currentYear = now.getFullYear() % 100;
    const currentMonth = now.getMonth() + 1;
    if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth))
      return t("expiryMustBeFuture", "Expiry date must be in the future");
    return "";
  };

  const validateCVC = (cvc: string, _cardNumber?: string): string => {
    if (!cvc) return t("cvcRequired", "CVC is required");
    const cleaned = cvc.replace(/\s/g, "");
    if (cleaned.length < 3 || cleaned.length > 4)
      return t("invalidCVC", "CVC must be 3-4 digits");
    if (!/^\d+$/.test(cleaned)) return t("cvcMustBeNumbers", "CVC must be numbers only");
    return "";
  };

  const validateName = (name: string): string => {
    if (!name || name.trim().length === 0)
      return t("cardNameRequired", "Cardholder name is required");
    if (name.trim().length < 2) return t("cardNameTooShort", "Cardholder name is too short");
    return "";
  };

  const handleInputFocus = ({ target }: React.FocusEvent<HTMLInputElement>) => {
    void target.name;
  };

  const handleInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    let value = target.value;
    const name = target.name;
    if (name === "number") value = formatCreditCardNumber(value);
    else if (name === "expiry") value = formatExpirationDate(value);
    else if (name === "cvc") value = formatCVC(value, value, val);

    const newVal = { ...val, [name]: value };
    setVal(newVal);

    let error = "";
    if (name === "number") error = validateCardNumber(value);
    else if (name === "expiry") error = validateExpiry(value);
    else if (name === "cvc") error = validateCVC(value, newVal.number);
    else if (name === "name") error = validateName(value);

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleInputBlur = ({ target }: React.FocusEvent<HTMLInputElement>) => {
    const name = target.name;
    setTouched((prev) => ({ ...prev, [name]: true }));

    let error = "";
    if (name === "number") error = validateCardNumber(val.number);
    else if (name === "expiry") error = validateExpiry(val.expiry);
    else if (name === "cvc") error = validateCVC(val.cvc, val.number);
    else if (name === "name") error = validateName(val.name);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors = {
      number: validateCardNumber(val.number),
      name: validateName(val.name),
      expiry: validateExpiry(val.expiry),
      cvc: validateCVC(val.cvc, val.number),
    };
    setErrors(newErrors);
    setTouched({ number: true, name: true, expiry: true, cvc: true });
    return !Object.values(newErrors).some((e) => e !== "");
  };

  const handlePayfortSubmit = () => {
    if (!validateForm()) return;

    const cardNumber = val.number.split(" ").join("");
    const expiry = val.expiry.split("/");
    const expiryStr = `${expiry[1]}${expiry[0]}`;

    const cardInputs = `
      <input type="hidden" name="card_number" value="${cardNumber}" />
      <input type="hidden" name="expiry_date" value="${expiryStr}" />
      <input type="hidden" name="card_security_code" value="${val.cvc}" />
      <input type="hidden" name="card_holder_name" value="${val.name}" />
    `;
    const bottomForm = `<button type="submit" id="pay_btn_form">Pay</button></form>`;
    const form = payForm + cardInputs + bottomForm;

    const div = document.getElementById("payment_form_div");
    if (div) {
      div.innerHTML = form;
      div.style.display = "block";
      const btn = document.getElementById("pay_btn_form");
      if (btn) (btn as HTMLButtonElement).click();
    }
  };

  return (
    <>
      <div className="yp-payment__methods">
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            className={
              active === method.id
                ? "yp-payment__method is-selected"
                : "yp-payment__method"
            }
            onClick={() => setActive(method.id)}
          >
            <span className="yp-payment__method-label">{method.name}</span>
            <img src={method.img} alt="" />
          </div>
        ))}
      </div>

      {active ? (
        <div className="yp-payment__card">
          <PayfortPaymentForm
            val={val}
            errors={errors}
            touched={touched}
            onInputChange={handleInputChange}
            onInputFocus={handleInputFocus}
            onInputBlur={handleInputBlur}
            onSubmit={handlePayfortSubmit}
          />
        </div>
      ) : null}
    </>
  );
}
